import React from 'react';
import { useReactTable, getCoreRowModel } from '@tanstack/react-table';
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import ApiLoader from '../../ui/ApiLoader';
import DataTablePagination from '../../common/DataTable/DataTablePagination';

const AuditLogTab = ({
  loading,
  auditLog,
  loadAuditLog,
  formatDate,
  auditLogPage,
  setAuditLogPage,
  auditLogLimit,
  auditLogTotal,
  loadingMoreAuditLogs
}) => {
  const paginationTable = useReactTable({
    data: auditLog,
    columns: [{ id: 'entry', accessorFn: (r) => r }],
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    rowCount: auditLogTotal,
    state: { pagination: { pageIndex: (auditLogPage || 1) - 1, pageSize: auditLogLimit } },
    onPaginationChange: (updater) => {
      const current = { pageIndex: (auditLogPage || 1) - 1, pageSize: auditLogLimit };
      const next = typeof updater === 'function' ? updater(current) : updater;
      setAuditLogPage(next.pageIndex + 1);
      loadAuditLog(next.pageIndex + 1, false);
    },
  });

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg overflow-hidden">
        <div className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-4 sm:mb-6">
            <div>
              <h2 className="text-lg sm:text-xl font-bold flex items-center text-gray-900 dark:text-white">
                <DocumentArrowDownIcon className="w-5 h-5 text-orange-500 mr-2" />
                CRM Activity Audit Trail
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Page {auditLogPage} • {auditLog.length} activities on this page
              </p>
            </div>
            <button
              onClick={() => loadAuditLog(1, false)}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs sm:text-sm flex items-center space-x-1.5 w-full sm:w-auto"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Refresh</span>
            </button>
          </div>

          {loading && auditLog.length > 0 && <ApiLoader variant="bar" />}
          {loading && auditLog.length === 0 ? (
            <ApiLoader label="Loading activities..." className="py-20" />
          ) : auditLog.length === 0 ? (
            <div className="text-center py-16">
              <DocumentArrowDownIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <h3 className="text-base font-medium text-gray-900 dark:text-white mb-1">No Activity Logs</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                All CRM operations will be tracked here
              </p>
            </div>
          ) : (
            <div aria-busy={loading} className={`space-y-2 transition-opacity ${loading ? 'pointer-events-none opacity-50' : ''}`}>
              {auditLog
                .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                .map((entry, index) => {
                // Parse old/new values
                let changedFields = [];
                try {
                  const oldVal = entry.old_value || entry.metadata?.old_value;
                  const newVal = entry.new_value || entry.metadata?.new_value;
                  
                  const oldValue = oldVal ? (typeof oldVal === 'string' ? JSON.parse(oldVal) : oldVal) : null;
                  const newValue = newVal ? (typeof newVal === 'string' ? JSON.parse(newVal) : newVal) : null;
                  
                  if (oldValue && newValue && typeof oldValue === 'object' && typeof newValue === 'object') {
                    // For SUBSCRIPTION_RENEWED, show all fields even if unchanged
                    if (entry.action_type === 'SUBSCRIPTION_RENEWED') {
                      changedFields = Object.keys(newValue).map(key => ({
                        field: key,
                        oldValue: oldValue[key],
                        newValue: newValue[key]
                      }));
                    } else {
                      // For other actions, only show changed fields
                      changedFields = Object.keys(newValue).filter(key => 
                        JSON.stringify(oldValue[key]) !== JSON.stringify(newValue[key])
                      ).map(key => ({
                        field: key,
                        oldValue: oldValue[key],
                        newValue: newValue[key]
                      }));
                    }
                  }
                } catch (e) {}

                return (
                  <div key={entry.log_id || index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-xs sm:text-sm">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3">
                      <div className="flex-1 min-w-0">
                        {/* Header with badges */}
                        <div className="flex flex-wrap items-center gap-2 mb-1.5">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300`}>
                            {entry.action_type?.replace(/_/g, ' ')}
                          </span>
                          
                          {entry.customer_name && entry.customer_name !== 'N/A' && (
                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                              {entry.customer_name}
                            </span>
                          )}
                        </div>
                        
                        {/* Description */}
                        <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 mb-2">
                          {/* {entry.action_description} */}
                        </p>
                        
                        {/* Changed Fields - Compact View */}
                        {changedFields.length > 0 && (
                          <div className="space-y-1">
                            {changedFields.map((change, idx) => (
                              <div key={idx} className="text-xs bg-gray-50 dark:bg-gray-800 rounded px-2 py-1 flex flex-col xs:flex-row xs:items-center justify-between gap-1 xs:gap-2">
                                <span className="font-medium text-gray-700 dark:text-gray-300 capitalize block xs:inline-block">
                                  {change.field.replace(/_/g, ' ')}:
                                </span>
                                <div className="flex flex-wrap items-center ml-0 xs:ml-2 gap-1">
                                  <span className="text-red-600 dark:text-red-400">
                                    {String(change.oldValue).substring(0, 50)}
                                    {String(change.oldValue).length > 50 ? '...' : ''}
                                  </span>
                                  <span className="text-gray-400 mx-1">→</span>
                                  <span className="text-green-600 dark:text-green-400">
                                    {String(change.newValue).substring(0, 50)}
                                    {String(change.newValue).length > 50 ? '...' : ''}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Footer info */}
                        <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-gray-500 dark:text-gray-400">
                          <span>{entry.performed_by}</span>
                          <span>•</span>
                          <span>{formatDate(entry.timestamp)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {auditLog.length > 0 && (
          <DataTablePagination table={paginationTable} loading={loadingMoreAuditLogs} />
        )}
      </div>  
    </div>
  );
};

export default AuditLogTab;
